import React, { useEffect, useRef, useState } from "react";
import {
  Eye,
  EyeSlash,
  ArrowRight,
  ShieldCheck,
  Airplane,
  CreditCard2Front,
  Sun,
  Moon,
  Person,
  Envelope,
} from "react-bootstrap-icons";
import {
  createSignup,
  getProfileDetails,
  updateProfile,
} from "../../services/Admin/authServices";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { authActions } from "../../store";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../assets/Logo1.png";

const Profile = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [profileDetails, setProfileDetails] = useState("");
  const [profileloading, setProfileLoading] = useState(false);

  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    getProfileDetails()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted?.data || [];
        setProfileDetails(newData);
      })
      .catch(() => {
        setProfileDetails("");
        setProfileLoading(false);
      })
      .finally(() => {
        setProfileLoading(false);
      });
  }, []);

  useEffect(() => {
    if (profileDetails) {
      setFormData({
        full_name: profileDetails?.full_name,
        email: profileDetails?.email,
      });
    }
  }, [profileDetails]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let errorObj = {};

    if (!formData.full_name) errorObj.full_name = "Full name is required";

    const password = formData.password?.trim();

    if (password) {
      if (password.length < 8) {
        errorObj.password = "Password must be at least 8 characters";
      } else if (!/[A-Z]/.test(password)) {
        errorObj.password = "Must include at least 1 uppercase letter";
      } else if (!/[a-z]/.test(password)) {
        errorObj.password = "Must include at least 1 lowercase letter";
      } else if (!/[0-9]/.test(password)) {
        errorObj.password = "Must include at least 1 number";
      } else if (!/[^A-Za-z0-9]/.test(password)) {
        errorObj.password = "Must include at least 1 special character";
      }

      // confirm password only if password exists
      if (!formData.confirm_password) {
        errorObj.confirm_password = "Please confirm your password";
      } else if (formData.password !== formData.confirm_password) {
        errorObj.confirm_password = "Passwords do not match";
      }
    }
    setErrors(errorObj);
    if (Object.keys(errorObj).length > 0) return;

    setIsLoading(true);

    let obj = {
      email: formData?.email || "",
      password: formData?.password,
      full_name: formData?.full_name,
      confirm_password: formData?.confirm_password,
    };

    setIsLoading(true);
    updateProfile(obj)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        notify.success(decrypted.message);
        setIsLoading(true);
        let userData = decrypted;
        if (userData) {
          Utils.setDetail(decrypted);
          Utils.setIntervalRemove();
          dispatch(authActions.login(decrypted));
        }
      })
      .catch((error) => {
        notify.error(Utils.getErrorMessage(error));
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className={`${isDarkMode ? "dark" : ""}`}>
      <div className="min-h-screen bg-slate-50 dark:bg-[#060B16] flex flex-col lg:flex-row overflow-x-hidden transition-colors duration-300">
        <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center bg-gradient-to-br from-slate-100 via-slate-200 to-white dark:from-[#0B1220] dark:via-[#111827] dark:to-black transition-colors duration-300">
          <div className="absolute top-[-100px] left-[-100px] w-[400px] h-[400px] bg-voyage-cyan/10 rounded-full blur-3xl opacity-60 dark:opacity-100" />
          <div className="absolute bottom-[-120px] right-[-120px] w-[420px] h-[420px] bg-voyage-cyan/5 rounded-full blur-3xl opacity-60 dark:opacity-100" />

          <div className="relative z-10 max-w-xl px-12">
            <div className="grid grid-cols-2 gap-5 mt-12">
              <FeatureCard
                icon={<Airplane size={30} />}
                title="Global Access"
                desc="Unlock premium business and luxury travel solutions."
              />
              <FeatureCard
                icon={<CreditCard2Front size={30} />}
                title="Optimization Engine"
                desc="Real-time conversion metrics tracking algorithm analytics."
              />
            </div>
          </div>
        </div>

        {/* RIGHT SIDE / SIGNUP MAIN BODY CONTAINER */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-8 lg:px-16 xl:px-24 py-6 relative">
          <div className="w-full max-w-md mx-auto">
            {/* Base Glassmorphism Signup Card */}
            <div className="bg-white border border-slate-200 dark:bg-white/5 dark:border-white/10 backdrop-blur-2xl rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 shadow-xl dark:shadow-2xl transition-all duration-300">
              <div className="mb-6 text-center lg:text-left">
                <h2 className="text-2xl sm:text-4xl font-black text-slate-900 dark:text-white mb-1.5">
                  Your Profile Settings
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-gray-300 ml-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    ref={nameRef}
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="e.g. John"
                    className="w-full bg-slate-50 text-slate-900 border border-slate-200 dark:bg-white/5 dark:text-white dark:border-white/10 rounded-xl px-4 py-3.5 placeholder:text-slate-400 dark:placeholder:text-gray-500 focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/10 transition-all outline-none text-sm"
                  />
                  {errors.full_name && (
                    <p className="text-red-500 dark:text-red-400 text-xs mt-0.5 ml-1">
                      {errors.full_name}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-gray-300 ml-1">
                    Email ID
                  </label>
                  <input
                    type="email"
                    ref={emailRef}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g. name@domain.com"
                    disabled
                    className="w-full bg-slate-50 text-slate-900 border border-slate-200 dark:bg-white/5 dark:text-white dark:border-white/10 rounded-xl px-4 py-3.5 placeholder:text-slate-400 dark:placeholder:text-gray-500 focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/10 transition-all outline-none text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-gray-300 ml-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      ref={passwordRef}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create secure password"
                      className="w-full bg-slate-50 text-slate-900 border border-slate-200 dark:bg-white/5 dark:text-white dark:border-white/10 rounded-xl px-4 py-3.5 pr-12 placeholder:text-slate-400 dark:placeholder:text-gray-500 focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/10 transition-all outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-voyage-cyan dark:text-gray-500 dark:hover:text-voyage-cyan transition-colors"
                    >
                      {showPassword ? (
                        <EyeSlash size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 dark:text-red-400 text-xs mt-0.5 ml-1">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-gray-300 ml-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      ref={confirmPasswordRef}
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      className="w-full bg-slate-50 text-slate-900 border border-slate-200 dark:bg-white/5 dark:text-white dark:border-white/10 rounded-xl px-4 py-3.5 pr-12 placeholder:text-slate-400 dark:placeholder:text-gray-500 focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/10 transition-all outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-voyage-cyan dark:text-gray-500 dark:hover:text-voyage-cyan transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeSlash size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <p className="text-red-500 dark:text-red-400 text-xs mt-0.5 ml-1">
                      {errors.confirm_password}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-voyage-cyan text-slate-900 dark:text-slate-950 font-black py-3.5 rounded-xl text-base shadow-xl shadow-voyage-cyan/25 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2.5 disabled:opacity-70 disabled:hover:scale-100 mt-4"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-slate-900/20 border-t-slate-900 rounded-full animate-spin" />
                      <span>Updating profile...</span>
                    </div>
                  ) : (
                    <>
                      Update
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-white/60 border border-slate-200/80 dark:bg-white/5 dark:border-white/10 backdrop-blur-xl rounded-3xl p-5 hover:bg-white dark:hover:bg-white/10 transition-all duration-200 cursor-default shadow-sm dark:shadow-none">
    <div className="text-voyage-cyan mb-4">{icon}</div>
    <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-1">
      {title}
    </h3>
    <p className="text-slate-500 dark:text-gray-400 text-sm leading-snug">
      {desc}
    </p>
  </div>
);

export default Profile;
